import React from "react";
import { cn } from "@/lib/utils";
import { NavLink } from "react-router-dom";
import { CircleSlash } from "lucide-react";

/**
 * Renders a sidebar navigation item that can be either an internal route (`NavLink`) or
 * an external link (`<a>`). Supports collapsed mode to show only the icon.
 *
 * @param {React.ReactNode} icon - Icon element to display
 * @param {string} label - Text label for the menu item
 * @param {string} to - Route path or external URL
 * @param {boolean} collapsed - Whether the sidebar is collapsed (hides label)
 * @param {boolean} [external=false] - If true, renders as an external `<a>` link
 * @param {boolean} [end=false] - If true, the link is only active on an exact path
 *   match (NavLink `end`). Used for landing items whose path is a prefix of siblings
 *   (e.g. /admin/compliance vs /admin/compliance/intake) so they don't stay highlighted.
 * @param {boolean} [unavailable=false] - The item's module is switched off fleet-wide.
 *   The link still works — a kill switch never closes a read — so this only changes how
 *   it looks: muted, with a struck-through circle and the reason on hover. An item that
 *   looks identical to a working one sends the tenant to a page of dead buttons with no
 *   warning, which is the complaint this answers.
 * @param {string} [unavailableReason] - Tooltip text for that state.
 * @param {string} [className] - Appended last via `cn()`, so a caller in a
 *   different context (e.g. the collapsed rail's hover flyout) can adjust
 *   the inactive-state color without a second copy of this component.
 */
export const SidebarItem = React.memo(
  ({ icon, label, to, collapsed, external = false, end = false, unavailable = false, unavailableReason = "", className }) => {
    const IconComponent = icon;

    // Collapsed mode has no room for a label, so the tooltip carries both.
    const title = unavailable ? (collapsed ? `${label} — ${unavailableReason}` : unavailableReason) : collapsed ? label : "";

    if (external) {
      return (
        <a
          href={to}
          target="_blank"
          rel="noopener noreferrer"
          title={collapsed ? label : ""}
          className={cn(
            "flex items-center rounded-md text-[11px] hover:text-sidebar-hoverText hover:bg-sidebar-hoverBg",
            collapsed ? "justify-center w-8 h-8 mx-auto" : "gap-2 px-2.5 py-1.5 w-full",
            className
          )}
        >
          <IconComponent size={14} />
          {!collapsed && <span>{label}</span>}
        </a>
      );
    }

    return (
      <NavLink
        to={to}
        end={end}
        title={title}
        className={({ isActive }) =>
          cn(
            "flex items-center rounded-md text-[11px] transition-colors duration-150",
            collapsed ? "justify-center w-8 h-8 mx-auto" : "gap-2 px-2.5 py-1.5 w-full",
            isActive
              ? "bg-background-tertiary text-text-primary"
              : "text-sidebar-text hover:text-sidebar-hoverText hover:bg-sidebar-hoverBg",
            className
          )
        }
      >
        {/* The dim lives on the icon, not on the whole row. `opacity-55` over
            `text-sidebar-text` — already a muted token — left the label barely
            legible, and a state you cannot read is not a state. A dimmer icon
            beside a full-strength label plus the amber slash says the same
            thing and reads in both themes. */}
        <IconComponent size={14} className={cn(unavailable && "opacity-50")} />
        {!collapsed && <span className={cn(unavailable && "flex-1")}>{label}</span>}
        {unavailable && !collapsed && <CircleSlash size={11} className="shrink-0 text-tone-amber" aria-hidden="true" />}
      </NavLink>
    );
  }
);
